import React, { useState, useEffect } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { TAG_LIST, TAG_CREATE, TAG_DELETE } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import './Page.css';
import './Tags.css';

function Tags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [tagToDelete, setTagToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(TAG_LIST.path), { method: TAG_LIST.method, headers })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        const list = data.data || data;
        if (Array.isArray(list)) {
          setTags(list);
          setListError('');
        } else {
          setTags([]);
          setListError('Invalid response from server.');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTags([]);
          setListError(err.message || 'Failed to load tags.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = (createName || '').trim().replace(/^#+/, '');
    if (!name) {
      setError('Enter a tag name.');
      return;
    }
    setError('');
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch(apiUrl(TAG_CREATE.path), {
        method: TAG_CREATE.method,
        headers,
        body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.data || data.id)) {
        const newTag = data.data || { id: data.id, name };
        setTags((prev) => [...prev, newTag]);
        setCreateName('');
      } else {
        setError(getApiErrorMessage(data, res.status, { default: 'Could not create tag. Please try again.' }));
      }
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveClick = (tag) => {
    setTagToDelete(tag);
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;
    const tag = tagToDelete;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(TAG_DELETE.pathFor(tag.id)), {
        method: TAG_DELETE.method,
        headers,
      });
      if (res.ok) {
        setTags((prev) => prev.filter((t) => t.id !== tag.id));
        setTagToDelete(null);
      }
    } catch {
      // keep list as is
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (!deleting) setTagToDelete(null);
  };

  return (
    <div className="page tags-page">
      <div className="tags-header">
        <h3>Tags</h3>
        <p className="tags-subtitle">Hashtag-style tags used across the system. Create and manage them here.</p>
      </div>

      <form onSubmit={handleCreate} className="tags-create-row">
        <label htmlFor="tags-create-input" className="tags-create-label">
          Tag name <span className="label-required" aria-hidden="true">*</span>
        </label>
        <div className="tags-create-wrap">
          <span className="tags-create-prefix">#</span>
          <input
            id="tags-create-input"
            type="text"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="e.g. kids, nursery, learning"
            disabled={creating}
            className="tags-create-input"
            autoComplete="off"
          />
          <button type="submit" className="tags-create-btn" disabled={creating}>
            <FiPlus className="tags-create-btn-icon" aria-hidden />
            {creating ? 'Adding…' : 'Add tag'}
          </button>
        </div>
        {error && <p className="tags-error" role="alert">{error}</p>}
      </form>

      <div className="tags-section">
        <h4 className="tags-list-title">Available tags</h4>
        {listError && <p className="tags-error" role="alert">{listError}</p>}
        {loading ? (
          <p className="tags-loading">Loading tags…</p>
        ) : tags.length === 0 ? (
          <p className="tags-empty">No tags yet. Add one above.</p>
        ) : (
          <div className="tags-chips">
            {tags.map((tag) => (
              <span key={tag.id} className="tags-chip">
                <span className="tags-chip-text">#{tag.name}</span>
                <button
                  type="button"
                  className="tags-chip-remove"
                  onClick={() => handleRemoveClick(tag)}
                  aria-label={`Remove tag ${tag.name}`}
                  title="Remove tag"
                >
                  <FiX className="tags-chip-x" aria-hidden />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {tagToDelete && (
        <div
          className="tags-delete-overlay"
          onClick={handleCancelDelete}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tags-delete-title"
          aria-describedby="tags-delete-desc"
        >
          <div className="tags-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h4 id="tags-delete-title" className="tags-delete-title">Delete tag</h4>
            <p id="tags-delete-desc" className="tags-delete-desc">
              Do you want to delete this tag?
            </p>
            <p className="tags-delete-tag-name">#{tagToDelete.name}</p>
            <div className="tags-delete-actions">
              <button
                type="button"
                className="tags-delete-btn tags-delete-btn-no"
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                No
              </button>
              <button
                type="button"
                className="tags-delete-btn tags-delete-btn-yes"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tags;
